import { z } from 'zod';
/**
 * El protocolo de 12 meses, como dato.
 *
 * Es la transcripción de la línea de tiempo de `pasos-12meses-protocolo.html`
 * —material de Fundación, fuente de verdad clínica del calendario—: **veintiocho
 * ítems con nombre propio**, cada uno con su día relativo al egreso.
 *
 * **Por qué la lista itemizada y no la cadencia del encabezado.** El documento se
 * contradice consigo mismo: el encabezado dice "36+ check-ins · semanales M1–M3,
 * quincenales M4–M6, mensuales M7–M12", y la línea de tiempo nombra trece
 * check-ins y saltea la semana 11 (D+77). Manda lo específico sobre el resumen
 * (confirmado 2026-08-24), y `6-meses.html` —el otro documento de Fundación— dice
 * textual lo mismo para el primer semestre: "sem 1–3 semanal, sem 5–10 semanal",
 * también sin semana 11.
 *
 * La cadencia del encabezado no se pierde: es alcanzable como **patrón de
 * intensidad** (`cadencia.ts`, UC-M08), que es lo que el propio protocolo manda
 * —"el UA define modalidad M4–M6: quincenal, mensual o alta parcial"— y lo que
 * `seguimiento.html` llama intensidad variable.
 *
 * **Los días son relativos al egreso, que es el día 0.** La fecha de calendario
 * la calcula el generador del plan con la fecha real de cada paciente.
 */
export const CLASES_DE_ITEM = ['microcheck', 'escala', 'teleconsulta'];
export const claseDeItemSchema = z.enum(CLASES_DE_ITEM);
/**
 * Las clases que el paciente responde: check-ins y evaluaciones. La teleconsulta
 * no está —el paciente no la contesta, la agenda (E5)—, así que no es un intento
 * de contacto para el silencio (ADR-018) ni un pendiente en su recorrido (P-79).
 */
export const CLASES_QUE_EL_PACIENTE_RESPONDE = ['microcheck', 'escala'];
const ESCALAS_FUNCIONALES = ['nms', 'cas_e', 'katz15'];
const ESCALAS_CON_CALIDAD_DE_VIDA = ['nms', 'cas_e', 'katz15', 'eq5d5l'];
/**
 * El árbol básico del NMS que acompaña a cada check-in (P5 del protocolo, D-02).
 *
 * `escalas-datos-fhir.md`, validado por Fundación: "8 hitos + árbol básico en
 * check-ins". El check-in cuenta como respondido cuando están el microcheck y el
 * NMS; el NMS es una aplicación propia de la escala, con su `QuestionnaireResponse`
 * y su `Observation`. **No pinta color**: el semáforo sale sólo de los hitos de
 * evaluación (R-08, validada por Fundación) hasta que Fundación defina otra cosa.
 */
export const NMS_DEL_CHECKIN = ['nms'];
/**
 * Los trece check-ins.
 *
 * Del primer semestre son los que el protocolo nombra por semana: 1, 2, 3, 5, 6,
 * 7, 9, 10 y 14. Las semanas 4, 8 y 12 no faltan — son las evaluaciones de D+28,
 * D+56 y D+84, que en esas semanas ocupan el lugar del check-in. La 11 no está en
 * ninguno de los dos documentos de Fundación.
 *
 * Del segundo semestre son mensuales, uno por mes sin evaluación: M5, M7, M9 y
 * M11. Los meses con evaluación (M8, M10, M12) tienen la evaluación como
 * contacto del mes.
 */
const CHECK_INS = [
    {
        codigo: 'checkin-s01',
        clase: 'microcheck',
        dia: 7,
        hito: 'D+7',
        formularioId: 'microcheck-semanal',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Primer control ambulatorio',
    },
    {
        codigo: 'checkin-s02',
        clase: 'microcheck',
        dia: 14,
        hito: 'D+14',
        formularioId: 'microcheck-semanal',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Check-in semana 2',
    },
    {
        codigo: 'checkin-s03',
        clase: 'microcheck',
        dia: 21,
        hito: 'D+21',
        formularioId: 'microcheck-semanal',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Check-in semana 3 · avisa la evaluación de D+28',
    },
    {
        codigo: 'checkin-s05',
        clase: 'microcheck',
        dia: 35,
        hito: 'D+35',
        formularioId: 'microcheck-semanal',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Check-in semana 5 · incorpora los ajustes de la teleconsulta',
    },
    {
        codigo: 'checkin-s06',
        clase: 'microcheck',
        dia: 42,
        hito: 'D+42',
        formularioId: 'microcheck-semanal',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Check-in semana 6',
    },
    {
        codigo: 'checkin-s07',
        clase: 'microcheck',
        dia: 49,
        hito: 'D+49',
        formularioId: 'microcheck-semanal',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Check-in semana 7 · avisa la evaluación de M2',
    },
    {
        codigo: 'checkin-s09',
        clase: 'microcheck',
        dia: 63,
        hito: 'D+63',
        formularioId: 'microcheck-semanal',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Check-in semana 9',
    },
    {
        codigo: 'checkin-s10',
        clase: 'microcheck',
        dia: 70,
        hito: 'D+70',
        formularioId: 'microcheck-semanal',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Check-in semana 10 · avisa la evaluación trimestral',
    },
    {
        codigo: 'checkin-s14',
        clase: 'microcheck',
        dia: 98,
        hito: 'D+98',
        formularioId: 'microcheck-semanal',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Check-in semana 14 · avisa la evaluación de D+120',
    },
    {
        codigo: 'checkin-m05',
        clase: 'microcheck',
        dia: 140,
        hito: 'M5',
        formularioId: 'microcheck-semanal',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Check-in de mantenimiento · avisa la evaluación semestral',
    },
    {
        codigo: 'checkin-m07',
        clase: 'microcheck',
        dia: 196,
        hito: 'M7',
        formularioId: 'microcheck-mensual',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Primer check-in del segundo semestre',
    },
    {
        codigo: 'checkin-m09',
        clase: 'microcheck',
        dia: 266,
        hito: 'M9',
        formularioId: 'microcheck-mensual-ampliado',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Check-in mensual · suma marcha sostenida y estado anímico',
    },
    {
        codigo: 'checkin-m11',
        clase: 'microcheck',
        dia: 336,
        hito: 'M11',
        formularioId: 'microcheck-mensual',
        instrumentos: NMS_DEL_CHECKIN,
        obligatorio: true,
        descripcion: 'Último check-in previo al cierre anual',
    },
];
/**
 * Las ocho evaluaciones con escalas.
 *
 * Son los ocho hitos de la serie longitudinal (`escalas-datos-fhir.md`): las tres
 * funcionales desde D+28, y el EQ-5D-5L desde D+120, que es su baseline.
 *
 * Seis de las ocho llevan además su **cuestionario de hito** (E3.25, D-01): las
 * preguntas "extra" que el protocolo pide en D+28, D+120, D+180, D+240, D+300 y
 * D+360. Se abre y vence con la misma ventana que las escalas, porque cuelga del
 * mismo ítem. M2 y M3 no tienen "extra" en el protocolo.
 */
const EVALUACIONES = [
    {
        codigo: 'eval-d28',
        clase: 'escala',
        dia: 28,
        hito: 'D+28',
        formularioId: 'cuestionario-d28',
        instrumentos: ESCALAS_FUNCIONALES,
        obligatorio: true,
        descripcion: 'Evaluación 30 días',
    },
    {
        codigo: 'eval-m02',
        clase: 'escala',
        dia: 56,
        hito: 'M2',
        instrumentos: ESCALAS_FUNCIONALES,
        obligatorio: true,
        descripcion: 'Evaluación 60 días · comparativa vs M1',
    },
    {
        codigo: 'eval-m03',
        clase: 'escala',
        dia: 84,
        hito: 'M3',
        instrumentos: ESCALAS_FUNCIONALES,
        obligatorio: true,
        descripcion: 'Evaluación trimestral · define la modalidad de M4–M6',
    },
    {
        codigo: 'eval-d120',
        clase: 'escala',
        dia: 120,
        hito: 'D+120',
        formularioId: 'cuestionario-d120',
        instrumentos: ESCALAS_CON_CALIDAD_DE_VIDA,
        obligatorio: true,
        descripcion: 'Evaluación D+120 · baseline del EQ-5D-5L',
    },
    {
        codigo: 'eval-d180',
        clase: 'escala',
        dia: 180,
        hito: 'D+180',
        formularioId: 'cuestionario-d180',
        instrumentos: ESCALAS_CON_CALIDAD_DE_VIDA,
        obligatorio: true,
        descripcion: 'Evaluación semestral · cierre del primer semestre',
    },
    {
        codigo: 'eval-d240',
        clase: 'escala',
        dia: 240,
        hito: 'D+240',
        formularioId: 'cuestionario-d240',
        instrumentos: ESCALAS_CON_CALIDAD_DE_VIDA,
        obligatorio: true,
        descripcion: 'Evaluación D+240',
    },
    {
        codigo: 'eval-d300',
        clase: 'escala',
        dia: 300,
        hito: 'D+300',
        formularioId: 'cuestionario-d300',
        instrumentos: ESCALAS_CON_CALIDAD_DE_VIDA,
        obligatorio: true,
        descripcion: 'Evaluación D+300 · proyección del cierre anual',
    },
    {
        codigo: 'eval-d360',
        clase: 'escala',
        dia: 360,
        hito: 'D+360',
        formularioId: 'cuestionario-d360',
        instrumentos: ESCALAS_CON_CALIDAD_DE_VIDA,
        obligatorio: true,
        descripcion: 'Evaluación anual completa · cierre del programa',
    },
];
/**
 * Las teleconsultas.
 *
 * Tres obligatorias —D+30, D+120 y el cierre anual— y cuatro "indicadas si…", que
 * son las únicas cuatro cosas del plan base que se pueden excluir.
 */
const TELECONSULTAS = [
    {
        codigo: 'telecons-d30',
        clase: 'teleconsulta',
        dia: 30,
        hito: 'D+30',
        obligatorio: true,
        descripcion: 'Teleconsulta 1 · revisión del dashboard de D+28',
    },
    {
        codigo: 'telecons-m03',
        clase: 'teleconsulta',
        dia: 84,
        hito: 'M3',
        obligatorio: false,
        descripcion: 'Teleconsulta de M3 · indicada si hay regresión o dudas clínicas',
    },
    {
        codigo: 'telecons-d120',
        clase: 'teleconsulta',
        dia: 120,
        hito: 'D+120',
        obligatorio: true,
        descripcion: 'Teleconsulta 2 · evaluación D+120 y baseline de calidad de vida',
    },
    {
        codigo: 'telecons-m06',
        clase: 'teleconsulta',
        dia: 180,
        hito: 'D+180',
        obligatorio: false,
        descripcion: 'Teleconsulta de cierre semestral · indicada si hay regresión',
    },
    {
        codigo: 'telecons-m08',
        clase: 'teleconsulta',
        dia: 240,
        hito: 'D+240',
        obligatorio: false,
        descripcion: 'Teleconsulta de M8 · indicada si hay regresión funcional',
    },
    {
        codigo: 'telecons-m10',
        clase: 'teleconsulta',
        dia: 300,
        hito: 'D+300',
        obligatorio: false,
        descripcion: 'Teleconsulta de M10 · preparación del cierre anual',
    },
    {
        codigo: 'telecons-d360',
        clase: 'teleconsulta',
        dia: 360,
        hito: 'D+360',
        obligatorio: true,
        descripcion: 'Teleconsulta de cierre anual · alta, extensión o derivación',
    },
];
/** El plan base completo, ordenado por día. */
export const PROTOCOLO_12_MESES = Object.freeze([...CHECK_INS, ...EVALUACIONES, ...TELECONSULTAS].sort((a, b) => a.dia - b.dia || a.clase.localeCompare(b.clase)));
/** Los ocho hitos de evaluación, en orden. Son las columnas de M-15. */
export const HITOS_DE_EVALUACION = Object.freeze(EVALUACIONES.map((item) => item.hito));
/** El último día del programa base. */
export const ULTIMO_DIA_DEL_PROTOCOLO = 360;
// ─────────────────────────────────────────────────────────────────────────────
// Extensión post-M12
// ─────────────────────────────────────────────────────────────────────────────
/**
 * La extensión, en días. El protocolo la define como "3 a 6 meses adicionales".
 */
export const EXTENSION_DIAS_MINIMO = 90;
export const EXTENSION_DIAS_MAXIMO = 180;
const DIAS_ENTRE_CHECKINS_DE_EXTENSION = 30;
/**
 * Los ítems de una extensión · **cierra P-45**.
 *
 * "Seguimiento trimestral (1 check-in/mes) por 3–6 meses adicionales si hay
 * indicación clínica" — la frase del protocolo se contradice a sí misma y manda
 * el paréntesis, que es el concreto: **un check-in por mes**. Con las
 * características del mes 11, que es lo que Fundación definió el 06/08:
 * consolidación, batería mensual del segundo semestre, sin escalas nuevas.
 *
 * **No hay evaluaciones ni teleconsultas en la extensión.** El protocolo no las
 * define, y agregarlas sería inventar calendario clínico.
 */
export function itemsDeExtension(diasDeExtension) {
    if (diasDeExtension < EXTENSION_DIAS_MINIMO || diasDeExtension > EXTENSION_DIAS_MAXIMO) {
        throw new Error(`La extensión va de ${EXTENSION_DIAS_MINIMO} a ${EXTENSION_DIAS_MAXIMO} días; llegó ${diasDeExtension}.`);
    }
    const items = [];
    const meses = Math.floor(diasDeExtension / DIAS_ENTRE_CHECKINS_DE_EXTENSION);
    for (let mes = 1; mes <= meses; mes += 1) {
        const dia = ULTIMO_DIA_DEL_PROTOCOLO + mes * DIAS_ENTRE_CHECKINS_DE_EXTENSION;
        items.push({
            codigo: `ext-checkin-${String(mes).padStart(2, '0')}`,
            clase: 'microcheck',
            dia,
            hito: `E+${mes}`,
            formularioId: 'microcheck-mensual',
            obligatorio: true,
            descripcion: `Check-in mensual de la extensión · mes ${mes}`,
        });
    }
    return items;
}
//# sourceMappingURL=protocolo.js.map